import { IFiltersList } from "@/src/types/applicationOnline";
import {
  IApplicantAccountingCreatePayload,
  IApplicantAccountingListItem,
  IApplicantAccountingListResponse,
  IApplicantAccountingUpdatePayload,
  IApplicantInfoCreatePayload,
  IApplicantInfoDetail,
  IApplicantInfoListItem,
  IApplicantInfoPayload,
} from "@/src/types/officeAccounting";
import { apiType } from "./apiType";

/** Response shape for GET /applicant-info/list and /applicant-info/search/filter. */
export interface IOfficeAccountingListResponse {
  data?: IApplicantInfoListItem[];
  count?: number;
}

const buildListParams = (limit: string, offset: number, office: string) => ({
  limit,
  offset,
  office,
});

const APPLICANT_ACCOUNTING_ALREADY_EXISTS = /applicant accounting already exists/i;

const parseApplicantAccountingApiError = (error: unknown): string => {
  const axiosError = error as { response?: { data?: { detail?: unknown } } };
  const detail = axiosError.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => (typeof item === "object" && item && "msg" in item ? String(item.msg) : JSON.stringify(item)))
      .join("; ");
  }
  if (detail != null) return JSON.stringify(detail);
  return "Could not create the Office Accounting record.";
};

export type ApplicantAccountingApproveResult =
  | { ok: true; mode: "created" | "updated" | "existing" }
  | { ok: false; message: string };

/** GET /applicant-info/list — как Online applications (limit, offset, office). */
export const officeAccountingPageRequests = {
  getList: async (limit: string, offset: number, office: string) => {
    try {
      return await apiType.main.get<IOfficeAccountingListResponse>("/applicant-info/list", buildListParams(limit, offset, office));
    } catch (error) {
      console.log("Office Accounting getList error:", error);
      return null;
    }
  },

  /** GET /applicant-accounting/list */
  getApplicantAccountingList: async (limit: string, offset: number, office: string) => {
    try {
      return await apiType.main.get<IApplicantAccountingListResponse>(
        "/applicant-accounting/list",
        buildListParams(limit, offset, office),
      );
    } catch (error) {
      console.log("Office Accounting getApplicantAccountingList error:", error);
      return null;
    }
  },

  /** GET /applicant-accounting/filters/list — available filter options for the selected office. */
  getFilterList: async (office: string) => {
    try {
      const normalizedOffice = office.trim() || "ALL";
      return await apiType.main.get<IFiltersList>(`/applicant-accounting/filters/list?office=${encodeURIComponent(normalizedOffice)}`);
    } catch (error) {
      console.log("Office Accounting getFilterList error:", error);
      return null;
    }
  },

  /** POST /applicant-accounting/filters/apply — filtered & paginated table data. */
  applyFilters: async (limit: string, offset: number, office: string, data: object = {}) => {
    try {
      const normalizedOffice = office.trim() || "ALL";
      return await apiType.main.post<IApplicantAccountingListResponse>(
        `/applicant-accounting/filters/apply?office=${encodeURIComponent(normalizedOffice)}&limit=${limit}&offset=${offset}`,
        data,
      );
    } catch (error) {
      console.log("Office Accounting applyFilters error:", error);
      return null;
    }
  },

  /** GET /applicant-info/search/filter — query, limit, offset, office в query. */
  searchFilter: async (limit: string, offset: number, office: string, query: string) => {
    try {
      return await apiType.main.get<IOfficeAccountingListResponse>("/applicant-info/search/filter", {
        ...buildListParams(limit, offset, office),
        query,
      });
    } catch (error) {
      console.log("Office Accounting searchFilter error:", error);
      return null;
    }
  },

  getByStudentInfoId: async (studentInfoId: number | string) => {
    try {
      return await apiType.main.get<IApplicantInfoDetail>(`/applicant-info/${studentInfoId}`);
    } catch (error) {
      console.log("Office Accounting getByStudentInfoId error:", error);
      return null;
    }
  },

  /** Reserved — not used in UI yet. */
  create: async (data: IApplicantInfoCreatePayload) => {
    try {
      await apiType.main.post("/applicant-info/create", data);
      return true;
    } catch (error) {
      console.log("Office Accounting create error:", error);
      return false;
    }
  },

  /** POST /applicant-accounting/create — Document Check approve → Office Accounting row. */
  createApplicantAccounting: async (data: IApplicantAccountingCreatePayload): Promise<{ ok: true } | { ok: false; message: string }> => {
    try {
      await apiType.main.post("/applicant-accounting/create", data);
      return { ok: true };
    } catch (error) {
      const message = parseApplicantAccountingApiError(error);
      const axiosError = error as { response?: { data?: unknown } };
      console.log("Office Accounting createApplicantAccounting error:", axiosError.response?.data ?? error);
      return { ok: false, message };
    }
  },

  findApplicantAccountingByApplicantId: async (
    applicantId: number,
    office: string,
  ): Promise<IApplicantAccountingListItem | null> => {
    try {
      const response = await apiType.main.get<IApplicantAccountingListResponse>(
        "/applicant-accounting/list",
        buildListParams("500", 0, office),
      );
      const items = Array.isArray(response?.data) ? response.data : [];
      return items.find((item) => item.applicant_id === applicantId) ?? null;
    } catch (error) {
      console.log("Office Accounting findApplicantAccountingByApplicantId error:", error);
      return null;
    }
  },

  /** Create accounting row or refresh admission fields when the applicant already has one. */
  createOrUpdateApplicantAccountingForApprove: async (
    data: IApplicantAccountingCreatePayload,
  ): Promise<ApplicantAccountingApproveResult> => {
    try {
      await apiType.main.post("/applicant-accounting/create", data);
      return { ok: true, mode: "created" };
    } catch (error) {
      const message = parseApplicantAccountingApiError(error);
      const axiosError = error as { response?: { data?: unknown } };
      console.log("Office Accounting createOrUpdateApplicantAccountingForApprove error:", axiosError.response?.data ?? error);

      if (!APPLICANT_ACCOUNTING_ALREADY_EXISTS.test(message)) {
        return { ok: false, message };
      }

      const existing = await officeAccountingPageRequests.findApplicantAccountingByApplicantId(data.applicant_id, data.form_office);
      if (!existing?.id) {
        return { ok: true, mode: "existing" };
      }

      const updated = await officeAccountingPageRequests.updateApplicantAccounting(existing.id, {
        admission_no: data.admission_no,
        data_admission: data.data_admission,
        general_status: data.general_status,
      });

      return updated == null ? { ok: true, mode: "existing" } : { ok: true, mode: "updated" };
    }
  },

  update: async (studentInfoId: number | string, data: IApplicantInfoPayload) => {
    try {
      return await apiType.main.put<IApplicantInfoDetail>(`/applicant-info/update/${studentInfoId}`, data, {
        params: { student_info_id: studentInfoId },
      });
    } catch (error) {
      console.log("Office Accounting update error:", error);
      return null;
    }
  },

  /** PUT /applicant-accounting/update — id передаётся в теле запроса. */
  updateApplicantAccounting: async (accountingId: number | string, data: IApplicantAccountingUpdatePayload) => {
    try {
      const parsedId = Number(accountingId);
      return await apiType.main.put("/applicant-accounting/update", {
        id: Number.isFinite(parsedId) ? parsedId : accountingId,
        ...data,
      });
    } catch (error) {
      console.log("Office Accounting updateApplicantAccounting error:", error);
      return null;
    }
  },

  delete: async (studentInfoId: number | string) => {
    try {
      await apiType.main.delete(`/applicant-info/delete/${studentInfoId}`, undefined, {
        params: { student_info_id: studentInfoId },
      });
      return true;
    } catch (error) {
      console.log("Office Accounting delete error:", error);
      return false;
    }
  },
};
